/**
 * TaskDetail — organism (CES standard)
 * Expanded capacity task detail: requested pools, dates, failure reason,
 * and the blocking-instances panel while WAITING_FOR_EVACUATION. Data and
 * polling come from the useCapacityTaskDetail hook.
 */

import { useCapacityTaskDetail } from '../../hooks/useCapacityTaskDetail';
import type { BlockingInstance } from '../../types/outposts';
import { Chip } from '../atoms/Chip';
import { DataTable, type DataTableColumn } from '../molecules/DataTable';

function formatDate(iso?: string): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

const BLOCKING_COLUMNS: DataTableColumn<BlockingInstance>[] = [
  { key: 'instanceId', header: 'Instance' },
  { key: 'accountId', header: 'Account' },
  { key: 'awsServiceName', header: 'Service' },
];

export function TaskDetail({ outpostId, taskId }: { outpostId: string; taskId: string }) {
  const { detail, blocking, error } = useCapacityTaskDetail(outpostId, taskId);

  if (error) return <p className="py-6 text-center text-sm text-red-600">{error}</p>;
  if (!detail) return <p className="py-6 text-center text-sm text-gray-500">Loading task…</p>;

  return (
    <div className="border-t border-gray-200 p-5" data-testid={`task-detail-${taskId}`}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">Requested pools</p>
          <div className="flex flex-wrap gap-2">
            {detail.requestedInstancePools.map((pool) => (
              <Chip key={pool.instanceType}>
                {pool.instanceType} × {pool.count}
              </Chip>
            ))}
          </div>
        </div>
        <div>
          <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">Dates</p>
          <p className="mb-1 text-sm text-gray-900">Created: {formatDate(detail.creationDate)}</p>
          <p className="mb-1 text-sm text-gray-900">
            Modified: {formatDate(detail.lastModifiedDate)}
          </p>
          {detail.completionDate && (
            <p className="mb-1 text-sm text-gray-900">
              Completed: {formatDate(detail.completionDate)}
            </p>
          )}
          {detail.taskActionOnBlockingInstances && (
            <p className="mb-1 text-sm text-gray-900">
              On blocking: {detail.taskActionOnBlockingInstances}
            </p>
          )}
        </div>
      </div>

      {detail.failed && (
        <p
          className="mt-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
          data-testid="task-failure"
        >
          {detail.failed.type}: {detail.failed.reason}
        </p>
      )}

      {detail.status === 'WAITING_FOR_EVACUATION' && (
        <div
          className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4"
          data-testid="blocking-panel"
        >
          <p className="mb-2 text-xs uppercase tracking-wide text-amber-800">
            Blocking instances that must stop before the task can proceed
          </p>
          {blocking.length === 0 ? (
            <p className="py-4 text-center text-sm text-gray-500">Loading blocking instances…</p>
          ) : (
            <DataTable
              data={blocking}
              columns={BLOCKING_COLUMNS}
              rowTestId={(row) => `blocking-${row.instanceId}`}
            />
          )}
        </div>
      )}
    </div>
  );
}
